import { useEffect } from "react";

const SITE = "https://www.thelegacyspa.com";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

// Lightweight SEO hook — sets unique title, description, OG/Twitter tags,
// canonical and optional JSON-LD structured data per route.
export function useSeo({ title, description, path = "/", jsonLd } = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    if (title) {
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    const url = `${SITE}${path}`;
    upsertMeta("property", "og:url", url);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", path);
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [title, description, path, jsonLd]);
}

// Organization / LocalBusiness schema placeholder.
// NOTE: intentionally omits phone, email, hours, price range, license and
// review rating until confirmed.
export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HealthAndBeautyBusiness",
  name: "Legacy Spa, LLC",
  slogan: "Where Wellness Meets Legacy.",
  description:
    "Massage therapy and wellness in Greater Houston — in-suite, mobile, and corporate wellness services.",
  parentOrganization: { "@type": "Organization", name: "Legacy Forge Collective, LLC" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "363 N Sam Houston Pkwy E, Suite 1440K",
    addressLocality: "Houston",
    addressRegion: "TX",
    postalCode: "77060",
    addressCountry: "US",
  },
  areaServed: "Greater Houston",
  url: "https://www.thelegacyspa.com",
  telephone: "+1-832-243-8853",
  email: "info@thelegacyspa.com",
  openingHoursSpecification: [
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Monday", opens: "08:00", closes: "12:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Tuesday", opens: "08:00", closes: "22:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Wednesday", opens: "08:00", closes: "12:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Thursday", opens: "08:00", closes: "12:30" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Friday", opens: "08:00", closes: "22:00" },
    { "@type": "OpeningHoursSpecification", dayOfWeek: "Saturday", opens: "14:00", closes: "22:00" },
  ],
  sameAs: ["https://www.instagram.com/legacyspawellness/"],
};
